// Projects Overview — two-tab landing surface (WI-11908).
//
// Replaces the previous list-only /projects page with a Dashboard + List
// experience adapted from the "Projects Overview.html" design-handoff bundle.
//
// - Dashboard tab: portfolio KPIs (totals/active/this week/this month/attention),
//   week strip + reporting list (which projects logged time this week), and a
//   status donut + mini stats panel.
// - List tab: powerful filter bar (search, status, customer, manager, reporting
//   window), active-filter chips with one-click removal, sortable table matching
//   the design's columns (Project / Customer / Status / Manager / Hours·Budget /
//   Progress / This week / Last reported / actions), density toggle.
//
// Both tabs read from the single GET /projects query — no new endpoint. The
// backend was extended in WI-11908 with two roll-up fields: hoursThisWeek
// (sum of worked_hrs Mon..today) and budgetHours (sum of phase-resource
// budget hours). The workspace rates card and the project drawer are reused
// unchanged from before.

import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Plus, Pencil, Archive, Search, X, Coins, ChevronDown, ChevronRight,
  RefreshCw, Settings2, Download, ArrowUpDown, ArrowUp, ArrowDown,
  LayoutDashboard, List as ListIcon,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import type { Project, FortnoxCustomer } from '@/types/api'
import RatesTable from '@/components/rates/RatesTable'
import { ProjectDrawer } from '@/modules/projects/ProjectDrawer'
import './projectsOverview.css'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function initials(name: string): string {
  return name.split(' ').filter(Boolean).map(p => p[0]).join('').toUpperCase().slice(0, 2)
}

// Stable hue per string for avatar/swatch backgrounds. Same input → same color.
function hueFor(s: string): number {
  let h = 0
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return h % 360
}

function avatarStyle(name: string): React.CSSProperties {
  const h = hueFor(name)
  return {
    background: `linear-gradient(135deg, oklch(0.62 0.14 ${h}), oklch(0.5 0.16 ${(h + 60) % 360}))`,
  }
}

function customerLogoStyle(name: string): React.CSSProperties {
  // Slightly more saturated; deterministic per customer.
  const h = hueFor(name || 'Internal')
  return {
    background: `linear-gradient(135deg, oklch(0.65 0.16 ${h}), oklch(0.5 0.18 ${(h + 40) % 360}))`,
  }
}

// Project status palette as RAG-flavoured swatches for the reporting list and
// the leading swatch in the table's Project cell.
const SWATCH_PALETTE = [
  'oklch(0.55 0.18 250)',  // blue
  'oklch(0.55 0.18 295)',  // purple
  'oklch(0.62 0.16 50)',   // orange
  'oklch(0.55 0.16 140)',  // green
  'oklch(0.55 0.16 25)',   // red
  'oklch(0.55 0.05 240)',  // slate
  'oklch(0.6 0.06 198)',   // teal
]
function swatchFor(projectId: number): string {
  return SWATCH_PALETTE[Math.abs(projectId) % SWATCH_PALETTE.length]
}

function fmtRelativeTime(days: number | null): string {
  if (days == null) return '—'
  if (days <= 0)   return 'today'
  if (days === 1)  return 'yesterday'
  if (days < 7)    return `${days}d ago`
  if (days < 30)   return `${Math.floor(days / 7)}w ago`
  return `${Math.floor(days / 30)}mo ago`
}

function fmtHours(n: number): string {
  return n.toLocaleString('sv-SE', { maximumFractionDigits: 1 })
}

// ISO Monday of the current week + 7-day strip.
function isoMonday(d: Date): Date {
  const x = new Date(d); x.setHours(0, 0, 0, 0)
  const dow = (x.getDay() + 6) % 7  // Mon=0
  x.setDate(x.getDate() - dow)
  return x
}

// ─── Status meta ─────────────────────────────────────────────────────────────

const STATUS_FILTERS: { value: Project['status'] | 'all'; label: string }[] = [
  { value: 'all',       label: 'All' },
  { value: 'active',    label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'on_hold',   label: 'On hold' },
]

const STATUS_PILL_CLASS: Record<Project['status'], string> = {
  active:    'po-pill-good',
  completed: 'po-pill-neutral',
  on_hold:   'po-pill-warn',
  archived:  'po-pill-neutral',
}

const STATUS_DONUT_COLOR: Record<Project['status'], string> = {
  active:    'var(--ns-ok)',
  completed: 'var(--ns-info)',
  on_hold:   'var(--ns-warn)',
  archived:  'var(--ns-text-subtle)',
}

// Reporting freshness buckets used by the List tab's "Reporting" filter.
type ReportingFilter = 'all' | 'thisWeek' | 'thisMonth' | 'stale14'

const REPORTING_FILTERS: { value: ReportingFilter; label: string }[] = [
  { value: 'all',       label: 'Anytime' },
  { value: 'thisWeek',  label: 'This week' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'stale14',   label: 'No activity 14d+' },
]

function passesReporting(p: Project, f: ReportingFilter): boolean {
  if (f === 'all')       return true
  if (f === 'thisWeek')  return p.hoursThisWeek > 0
  if (f === 'thisMonth') return p.daysSinceWorkedOn != null && p.daysSinceWorkedOn <= 30
  if (f === 'stale14')   return p.daysSinceWorkedOn == null || p.daysSinceWorkedOn >= 14
  return true
}

// ─── Page ────────────────────────────────────────────────────────────────────

type Tab = 'dashboard' | 'list'

export default function ProjectsPage() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('dashboard')

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn:  () => api.get<Project[]>('/projects'),
    staleTime: 5 * 60 * 1000,
  })

  const { data: customers = [] } = useQuery({
    queryKey: ['fortnox', 'customers'],
    queryFn:  () => api.get<FortnoxCustomer[]>('/fortnox/customers'),
  })

  // Drawer state (shared between both tabs — Dashboard never opens the drawer
  // today, but it's wired so we can add a "+New" affordance there later.)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing,    setEditing]    = useState<Project | null>(null)
  const openCreate = () => { setEditing(null); setDrawerOpen(true) }
  const openEdit   = (p: Project) => { setEditing(p); setDrawerOpen(true) }

  const archiveMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/projects/${id}`),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  })

  const copyMutation = useMutation({
    mutationFn: () => api.post<{ copied: number; total: number }>('/fortnox/projects/copy', {}),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  })

  const listCount = projects.length

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Workspace</div>
          <h1 className="page-title">Projects</h1>
          <div className="page-subtitle">
            All client &amp; internal projects · time being reported on right now
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn btn-ghost"
            style={{ gap: 6 }}
            onClick={() => copyMutation.mutate()}
            disabled={copyMutation.isPending}
            title="Copy all active Fortnox projects into Forge as new projects"
          >
            <RefreshCw size={13} style={{ animation: copyMutation.isPending ? 'spin 0.8s linear infinite' : undefined }} />
            {copyMutation.isPending ? 'Copying…' : 'Copy from Fortnox'}
          </button>
          <Link to="/projects/manage" className="btn btn-ghost" style={{ gap: 6, textDecoration: 'none' }}>
            <Settings2 size={13} />Manage
          </Link>
          <button className="btn btn-brand" style={{ gap: 6 }} onClick={openCreate}>
            <Plus size={14} />New project
          </button>
        </div>
      </div>

      {copyMutation.isSuccess && (
        <div style={{
          background: 'var(--ns-ok-soft)', border: '1px solid var(--ns-ok)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 16,
          fontSize: 13, color: 'var(--ns-ok)',
        }}>
          Copied {copyMutation.data?.copied ?? 0} new project{copyMutation.data?.copied !== 1 ? 's' : ''} from Fortnox
          ({copyMutation.data?.total ?? 0} total in Fortnox).
        </div>
      )}

      {/* Tabs */}
      <div className="po-ptabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'dashboard'}
          className={tab === 'dashboard' ? 'on' : ''}
          onClick={() => setTab('dashboard')}
        >
          <LayoutDashboard size={15} />Dashboard
        </button>
        <button
          role="tab"
          aria-selected={tab === 'list'}
          className={tab === 'list' ? 'on' : ''}
          onClick={() => setTab('list')}
        >
          <ListIcon size={15} />List <span className="count">{listCount}</span>
        </button>
      </div>

      {isLoading ? (
        <div className="muted" style={{ textAlign: 'center', paddingTop: 32, fontSize: 13 }}>Loading…</div>
      ) : tab === 'dashboard' ? (
        <ProjectsDashboard projects={projects} />
      ) : (
        <ProjectsListTab
          projects={projects}
          onEdit={openEdit}
          onArchive={p => {
            if (confirm(`Archive "${p.name}"?`)) archiveMutation.mutate(p.id)
          }}
        />
      )}

      {drawerOpen && (
        <ProjectDrawer
          project={editing}
          customers={customers}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['projects'] })
            setDrawerOpen(false)
          }}
        />
      )}
    </div>
  )
}

// ─── Dashboard tab ───────────────────────────────────────────────────────────

function ProjectsDashboard({ projects }: { projects: Project[] }) {
  // KPIs — pure client-side aggregations off the existing /projects payload.
  const total = projects.length
  const byStatus: Record<Project['status'], number> = { active: 0, completed: 0, on_hold: 0, archived: 0 }
  for (const p of projects) byStatus[p.status]++

  const active = byStatus.active
  const reportingThisWeek = projects.filter(p => p.hoursThisWeek > 0).length
  const reportingThisMonth = projects.filter(p => p.daysSinceWorkedOn != null && p.daysSinceWorkedOn <= 30).length
  const totalHoursThisWeek = projects.reduce((s, p) => s + p.hoursThisWeek, 0)

  // Need-attention heuristic: active + over budget OR active + no activity 14d+.
  const needsAttention = projects.filter(p =>
    p.status === 'active' && (
      (p.budgetHours != null && p.totalHours > p.budgetHours) ||
      (p.daysSinceWorkedOn != null && p.daysSinceWorkedOn >= 14)
    )
  )

  // Reporting list — projects with hoursThisWeek > 0, ordered by hours desc.
  const reportingList = projects
    .filter(p => p.hoursThisWeek > 0)
    .sort((a, b) => b.hoursThisWeek - a.hoursThisWeek)

  // Week strip — 7 days starting from this week's Monday. We don't have
  // per-day project hours, so the day totals can't be computed from the
  // /projects payload alone. Filed as tech-debt (#11909+). For now we hide
  // the per-day hours; the design still makes sense as a visual anchor.
  const monday = isoMonday(new Date())
  const todayIdx = (new Date().getDay() + 6) % 7
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i)
    return {
      dow: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
      num: d.getDate(),
      weekend: i >= 5,
      today: i === todayIdx,
    }
  })

  return (
    <>
      {/* KPI strip */}
      <section className="po-kpis">
        <div className="po-kpi">
          <div className="po-kpi-label">Total projects</div>
          <div className="po-kpi-val">{total}</div>
          <div className="po-kpi-stack" aria-hidden="true">
            {(['active', 'on_hold', 'completed', 'archived'] as Project['status'][]).map(s => {
              const count = byStatus[s]
              if (!count) return null
              const pct = (count / Math.max(total, 1)) * 100
              return <div key={s} style={{ width: `${pct}%`, background: STATUS_DONUT_COLOR[s] }} title={`${s} ${count}`} />
            })}
          </div>
          <div className="po-kpi-legend">
            <span style={{ color: 'var(--ns-ok)' }}>{byStatus.active} Active</span>
            <span style={{ color: 'var(--ns-warn)' }}>{byStatus.on_hold} On hold</span>
            <span style={{ color: 'var(--ns-info)' }}>{byStatus.completed} Done</span>
          </div>
        </div>

        <div className="po-kpi">
          <div className="po-kpi-label">Active</div>
          <div className="po-kpi-val">{active}</div>
          <div className="po-kpi-sub">
            {projects.filter(p => p.status === 'active' && p.customerId == null).length} internal ·{' '}
            {new Set(projects.filter(p => p.status === 'active' && p.projectManagerId != null).map(p => p.projectManagerId)).size} managers
          </div>
        </div>

        <div className="po-kpi featured">
          <div className="po-kpi-label">Reporting this week</div>
          <div className="po-kpi-val">{reportingThisWeek} <span className="unit">of {active} active</span></div>
          <div className="po-kpi-sub">{fmtHours(totalHoursThisWeek)} h logged so far</div>
        </div>

        <div className="po-kpi">
          <div className="po-kpi-label">Reporting this month</div>
          <div className="po-kpi-val">{reportingThisMonth} <span className="unit">of {active} active</span></div>
          <div className="po-kpi-sub">Projects with time in the last 30d</div>
        </div>

        <div className="po-kpi">
          <div className="po-kpi-label">Need attention</div>
          <div className="po-kpi-val" style={{ color: needsAttention.length > 0 ? 'var(--ns-warn)' : undefined }}>
            {needsAttention.length}
          </div>
          <div className={`po-kpi-sub ${needsAttention.length > 0 ? 'warn' : ''}`}>
            {needsAttention.length === 0 ? 'All on track' : 'Over budget or 14d+ idle'}
          </div>
        </div>
      </section>

      {/* Reporting list + status panel */}
      <section className="po-dash-row">
        <div className="card">
          <div className="po-card-head">
            <div>
              <div className="po-card-title">Projects reporting time this week</div>
              <div className="po-card-sub">
                {reportingList.length} {reportingList.length === 1 ? 'project' : 'projects'} ·{' '}
                {fmtHours(totalHoursThisWeek)} hours
              </div>
            </div>
          </div>

          <div className="po-week-strip" aria-label="Hours per day this week">
            {weekDays.map(d => (
              <div key={d.num} className={`d${d.today ? ' today' : ''}${d.weekend ? ' weekend' : ''}`}>
                <div className="dow">{d.dow}</div>
                <div className="num">{d.num}</div>
                <div className="h">{d.weekend ? '—' : ''}</div>
              </div>
            ))}
          </div>

          <div className="po-rep-list">
            {reportingList.length === 0 ? (
              <div className="po-empty">
                <div className="big">No time reported yet this week</div>
                <div>As soon as someone logs time it will show up here.</div>
              </div>
            ) : reportingList.map(p => {
              const planTotal = p.budgetHours ?? null
              const pctOfPlan = planTotal != null && planTotal > 0
                ? Math.min(100, Math.round((p.totalHours / planTotal) * 100))
                : null
              const tone =
                pctOfPlan == null ? 'muted' :
                pctOfPlan >= 100  ? 'danger' :
                pctOfPlan >= 80   ? 'warn'   : 'good'
              return (
                <div key={p.id} className="po-rep-row">
                  <div className="po-swatch" style={{ background: swatchFor(p.id) }} />
                  <div className="po-rep-proj">
                    <Link
                      to="/projects/$id"
                      params={{ id: String(p.id) }}
                      className="name"
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {p.name}
                    </Link>
                    <span className="a">
                      {p.customerName ?? 'Internal'}
                      {p.projectManagerName && <> · {p.projectManagerName}</>}
                    </span>
                  </div>
                  <ResourceAvatars names={p.resourceNames} count={p.resourceCount} />
                  <div className="po-rep-hours">
                    {fmtHours(p.hoursThisWeek)}<span className="unit">h</span>
                  </div>
                  <div>
                    <div className={`po-pbar ${tone}`}>
                      <div style={{ width: `${pctOfPlan ?? 0}%`, background: swatchFor(p.id) }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ns-text-subtle)', marginTop: 4 }}>
                      {planTotal != null
                        ? `${pctOfPlan}% of ${fmtHours(planTotal)}h budget`
                        : 'No budget set'}
                    </div>
                  </div>
                  <div className={`po-rep-when${p.daysSinceWorkedOn != null && p.daysSinceWorkedOn <= 1 ? ' fresh' : ''}`}>
                    {fmtRelativeTime(p.daysSinceWorkedOn)}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <StatusPanel projects={projects} byStatus={byStatus} />
      </section>
    </>
  )
}

// ─── Status donut + mini stats ───────────────────────────────────────────────

function StatusPanel({
  projects, byStatus,
}: {
  projects: Project[]
  byStatus: Record<Project['status'], number>
}) {
  const total = projects.length
  // Donut: circumference of r=15.915 ≈ 100, so dasharray is in % units.
  const ORDER: Project['status'][] = ['active', 'on_hold', 'completed', 'archived']
  let offset = 25  // start at 12 o'clock
  const arcs = ORDER.map(s => {
    const count = byStatus[s]
    const pct = total > 0 ? (count / total) * 100 : 0
    const dash = `${pct.toFixed(1)} ${(100 - pct).toFixed(1)}`
    const arc = { color: STATUS_DONUT_COLOR[s], dash, offset, status: s, count, pct }
    offset -= pct
    return arc
  })

  const totalHoursAll  = projects.reduce((s, p) => s + p.totalHours, 0)
  const totalHoursWeek = projects.reduce((s, p) => s + p.hoursThisWeek, 0)

  return (
    <div className="card" style={{ display: 'grid', gridTemplateRows: 'auto 1fr' }}>
      <div>
        <div className="po-card-head">
          <div>
            <div className="po-card-title">Status breakdown</div>
            <div className="po-card-sub">
              {total} {total === 1 ? 'project' : 'projects'} across{' '}
              {new Set(projects.map(p => p.customerId).filter(c => c != null)).size} customers
            </div>
          </div>
        </div>
        <div style={{ padding: '16px 18px' }}>
          <div className="po-donut-wrap">
            <div className="po-donut">
              <svg viewBox="0 0 42 42" width="132" height="132">
                <circle cx="21" cy="21" r="15.915" fill="var(--ns-surface)" />
                {arcs.filter(a => a.count > 0).map(a => (
                  <circle
                    key={a.status}
                    cx="21" cy="21" r="15.915"
                    fill="transparent"
                    stroke={a.color}
                    strokeWidth="6"
                    strokeDasharray={a.dash}
                    strokeDashoffset={a.offset}
                  />
                ))}
              </svg>
              <div className="po-donut-center">
                <div>
                  <div className="big">{total}</div>
                  <div className="lbl">Projects</div>
                </div>
              </div>
            </div>
            <div className="po-donut-legend">
              {ORDER.map(s => {
                const count = byStatus[s]
                if (!count) return null
                const pct = Math.round((count / Math.max(total, 1)) * 100)
                const label = s === 'on_hold' ? 'On hold' : s.charAt(0).toUpperCase() + s.slice(1)
                return (
                  <div className="li" key={s}>
                    <div className="sw" style={{ background: STATUS_DONUT_COLOR[s] }} />
                    <div className="lab">{label}</div>
                    <div className="ct"><b>{count}</b> · {pct}%</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="po-mini-stats">
        <div className="po-mini-stat">
          <div className="lbl">Hours · this week</div>
          <div className="val">{fmtHours(totalHoursWeek)} <span className="unit">h</span></div>
        </div>
        <div className="po-mini-stat">
          <div className="lbl">Billable total</div>
          <div className="val">{fmtHours(totalHoursAll)} <span className="unit">h</span></div>
        </div>
      </div>
    </div>
  )
}

// ─── Resource avatars (used by reporting list + project cell) ────────────────

function ResourceAvatars({ names, count }: { names: string | null; count: number }) {
  if (count === 0) return <span className="muted" style={{ fontSize: 12 }}>—</span>
  const list  = names ? names.split(',').filter(Boolean) : []
  const show  = list.slice(0, 4)
  const extra = count - show.length
  return (
    <div className="po-av-stack">
      {show.map((name, i) => (
        <div key={i} className="av" title={name} style={avatarStyle(name)}>{initials(name)}</div>
      ))}
      {extra > 0 && <span className="more">+{extra}</span>}
    </div>
  )
}

// ─── List tab ────────────────────────────────────────────────────────────────

type SortKey = 'name' | 'customer' | 'status' | 'manager' | 'hours' | 'progress' | 'week' | 'reported'
type SortDir = 'asc' | 'desc'

function ProjectsListTab({
  projects, onEdit, onArchive,
}: {
  projects: Project[]
  onEdit:    (p: Project) => void
  onArchive: (p: Project) => void
}) {
  // Filters
  const [search,     setSearch]     = useState('')
  const [status,     setStatus]     = useState<Project['status'] | 'all'>('all')
  const [customer,   setCustomer]   = useState<string>('')   // customer name (or 'Internal' for null customer)
  const [manager,    setManager]    = useState<string>('')   // manager name
  const [reporting,  setReporting]  = useState<ReportingFilter>('all')
  const [sortKey,    setSortKey]    = useState<SortKey>('name')
  const [sortDir,    setSortDir]    = useState<SortDir>('asc')
  const [density,    setDensity]    = useState<'comfortable' | 'compact'>('comfortable')

  const customerOptions = useMemo(() => {
    const set = new Set<string>()
    let hasInternal = false
    for (const p of projects) {
      if (p.customerName) set.add(p.customerName)
      else hasInternal = true
    }
    const sorted = Array.from(set).sort((a, b) => a.localeCompare(b, 'sv'))
    return hasInternal ? ['Internal', ...sorted] : sorted
  }, [projects])

  const managerOptions = useMemo(() => {
    const set = new Set<string>()
    for (const p of projects) if (p.projectManagerName) set.add(p.projectManagerName)
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'sv'))
  }, [projects])

  const q = search.toLowerCase().trim()
  const visible = useMemo(() => {
    const filtered = projects.filter(p =>
      (status === 'all' || p.status === status) &&
      (!customer ||
        (customer === 'Internal' ? p.customerId == null : p.customerName === customer)) &&
      (!manager || p.projectManagerName === manager) &&
      passesReporting(p, reporting) &&
      (!q ||
        p.name.toLowerCase().includes(q) ||
        (p.customerName ?? '').toLowerCase().includes(q) ||
        (p.projectManagerName ?? '').toLowerCase().includes(q) ||
        (p.fortnoxProjectNumber ?? '').includes(q))
    )
    return filtered.slice().sort((a, b) => sortCompare(a, b, sortKey, sortDir))
  }, [projects, status, customer, manager, reporting, q, sortKey, sortDir])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc') }
  }

  const resetFilters = () => {
    setSearch(''); setStatus('all'); setCustomer(''); setManager(''); setReporting('all')
  }

  const activeFilterChips: { label: string; clear: () => void }[] = []
  if (search)               activeFilterChips.push({ label: `Search: "${search}"`, clear: () => setSearch('') })
  if (status !== 'all')     activeFilterChips.push({ label: `Status: ${statusLabel(status)}`, clear: () => setStatus('all') })
  if (customer)             activeFilterChips.push({ label: `Customer: ${customer}`,         clear: () => setCustomer('') })
  if (manager)              activeFilterChips.push({ label: `Manager: ${manager}`,           clear: () => setManager('') })
  if (reporting !== 'all')  activeFilterChips.push({
    label: `Reporting: ${REPORTING_FILTERS.find(r => r.value === reporting)?.label}`,
    clear: () => setReporting('all'),
  })

  return (
    <>
      <WorkspaceRatesCard />

      {/* Filter bar */}
      <section className="po-filter-bar">
        <div className={`po-filt search${search ? ' has-value' : ''}`}>
          <span className="magnify"><Search size={14} /></span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search project, customer, manager…"
          />
        </div>
        <SelectFilter label="Status" value={status} onChange={v => setStatus(v as Project['status'] | 'all')}
          options={[{ value: 'all', label: 'All' }, ...STATUS_FILTERS.filter(f => f.value !== 'all').map(f => ({ value: f.value, label: f.label }))]} />
        <SelectFilter label="Customer" value={customer} onChange={setCustomer}
          options={[{ value: '', label: 'All' }, ...customerOptions.map(c => ({ value: c, label: c }))]} />
        <SelectFilter label="Manager" value={manager} onChange={setManager}
          options={[{ value: '', label: 'All' }, ...managerOptions.map(m => ({ value: m, label: m }))]} />
        <SelectFilter label="Reporting" value={reporting} onChange={v => setReporting(v as ReportingFilter)}
          options={REPORTING_FILTERS.map(r => ({ value: r.value, label: r.label }))} />
        <button className="po-filt-clear" onClick={resetFilters} title="Clear all filters">
          <X size={13} />Reset
        </button>
      </section>

      {activeFilterChips.length > 0 && (
        <div className="po-fchips">
          <span className="label">Active filters:</span>
          {activeFilterChips.map(c => (
            <span key={c.label} className="po-fchip">
              {c.label}
              <button onClick={c.clear} aria-label="Clear">×</button>
            </span>
          ))}
        </div>
      )}

      {/* Table */}
      <section className="po-table-wrap">
        <div className="po-table-toolbar">
          <div>
            <span className="po-ttitle">Projects</span>
            <span className="po-tcount">{visible.length} of {projects.length} shown</span>
          </div>
          <div className="row" style={{ gap: 6 }}>
            <div className="po-density" role="tablist" aria-label="Row density">
              <button
                className={density === 'comfortable' ? 'on' : ''}
                onClick={() => setDensity('comfortable')}
                aria-label="Comfortable"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 4h10M2 7h10M2 10h10" /></svg>
              </button>
              <button
                className={density === 'compact' ? 'on' : ''}
                onClick={() => setDensity('compact')}
                aria-label="Compact"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 3.5h10M2 6h10M2 8.5h10M2 11h10" /></svg>
              </button>
            </div>
            <button className="btn btn-ghost btn-sm" title="Export to CSV (not yet implemented)" disabled>
              <Download size={13} />Export
            </button>
          </div>
        </div>

        <table className={`po-gtable${density === 'compact' ? ' compact' : ''}`}>
          <thead>
            <tr>
              <th><SortableHead label="Project"   col="name"     sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} /></th>
              <th><SortableHead label="Customer"  col="customer" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} /></th>
              <th><SortableHead label="Status"    col="status"   sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} /></th>
              <th><SortableHead label="Manager"   col="manager"  sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} /></th>
              <th className="r"><SortableHead label="Hours / Budget" col="hours"    sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} /></th>
              <th className="r"><SortableHead label="Progress"       col="progress" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} /></th>
              <th className="r"><SortableHead label="This week"      col="week"     sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} /></th>
              <th className="r"><SortableHead label="Last reported"  col="reported" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} /></th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {visible.map(p => <ListRow key={p.id} p={p} onEdit={onEdit} onArchive={onArchive} />)}
            {visible.length === 0 && projects.length > 0 && (
              <tr>
                <td colSpan={9}>
                  <div className="po-empty">
                    <div className="big">No projects match the current filters</div>
                    <div>Try removing one of the chips above or hit Reset.</div>
                  </div>
                </td>
              </tr>
            )}
            {projects.length === 0 && (
              <tr>
                <td colSpan={9}>
                  <div className="po-empty">
                    <div className="big">No projects yet</div>
                    <div>Create one to get started, or use "Copy from Fortnox".</div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </>
  )
}

function statusLabel(s: Project['status']): string {
  return s === 'on_hold' ? 'On hold' : s.charAt(0).toUpperCase() + s.slice(1)
}

function sortCompare(a: Project, b: Project, key: SortKey, dir: SortDir): number {
  const mul = dir === 'asc' ? 1 : -1
  const str = (s: string | null, fallback = '￿') => s ?? fallback
  switch (key) {
    case 'name':     return mul * str(a.name).localeCompare(str(b.name), 'sv')
    case 'customer': return mul * str(a.customerName).localeCompare(str(b.customerName), 'sv')
    case 'status':   return mul * a.status.localeCompare(b.status)
    case 'manager':  return mul * str(a.projectManagerName).localeCompare(str(b.projectManagerName), 'sv')
    case 'hours':    return mul * (a.totalHours - b.totalHours)
    case 'progress': {
      const pa = a.budgetHours && a.budgetHours > 0 ? a.totalHours / a.budgetHours : -1
      const pb = b.budgetHours && b.budgetHours > 0 ? b.totalHours / b.budgetHours : -1
      return mul * (pa - pb)
    }
    case 'week':     return mul * (a.hoursThisWeek - b.hoursThisWeek)
    case 'reported': {
      const da = a.daysSinceWorkedOn ?? Number.POSITIVE_INFINITY
      const db = b.daysSinceWorkedOn ?? Number.POSITIVE_INFINITY
      return mul * (da - db)
    }
  }
}

// ─── Table row + helpers ─────────────────────────────────────────────────────

function ListRow({ p, onEdit, onArchive }: {
  p: Project
  onEdit:    (p: Project) => void
  onArchive: (p: Project) => void
}) {
  const pct = p.budgetHours != null && p.budgetHours > 0
    ? Math.round((p.totalHours / p.budgetHours) * 100)
    : null
  const over = pct != null && pct > 100
  const tone = pct == null ? '' : pct >= 100 ? 'warn' : pct >= 80 ? 'warn' : pct >= 50 ? '' : 'good'

  return (
    <tr>
      <td>
        <div className="po-proj-cell">
          <span className="po-swatch" style={{ background: swatchFor(p.id), height: 30 }} />
          <div className="col">
            <span className="name">
              <Link to="/projects/$id" params={{ id: String(p.id) }}>{p.name}</Link>
            </span>
            {p.fortnoxProjectNumber && (
              <span className="a mono">#{p.fortnoxProjectNumber}</span>
            )}
          </div>
        </div>
      </td>
      <td>
        {p.customerName ? (
          <div className="po-cust-cell">
            <div className="ph" style={customerLogoStyle(p.customerName)}>{initials(p.customerName)}</div>
            <span>{p.customerName}</span>
          </div>
        ) : (
          <span className="muted" style={{ fontStyle: 'italic' }}>Internal</span>
        )}
      </td>
      <td><span className={`po-pill ${STATUS_PILL_CLASS[p.status]}`}>{statusLabel(p.status)}</span></td>
      <td>
        {p.projectManagerName ? (
          <div className="po-mgr-cell">
            <div className="av" style={avatarStyle(p.projectManagerName)}>{initials(p.projectManagerName)}</div>
            <span>{p.projectManagerName}</span>
          </div>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td className="r">
        <div className={`po-hb-cell${over ? ' over' : ''}`}>
          <span className="h">{fmtHours(p.totalHours)}</span>{' '}
          <span className="b">{p.budgetHours != null && p.budgetHours > 0 ? `/ ${fmtHours(p.budgetHours)} h` : '/ — h'}</span>
        </div>
      </td>
      <td className="r">
        <div className="po-prog-cell">
          {pct == null ? (
            <span className="v muted">—</span>
          ) : (
            <>
              <div className={`po-pbar ${tone}`}><div style={{ width: `${Math.min(100, pct)}%` }} /></div>
              <span className={`v${over ? ' over' : ''}`}>{pct}%</span>
            </>
          )}
        </div>
      </td>
      <td className="r">
        <div className={`po-week-cell${p.hoursThisWeek === 0 ? ' zero' : ''}`}>
          {fmtHours(p.hoursThisWeek)}<span className="unit">h</span>
        </div>
      </td>
      <td className="r" style={{ color: p.daysSinceWorkedOn != null && p.daysSinceWorkedOn <= 1 ? 'var(--ns-ok)' : 'var(--ns-text-subtle)' }}>
        {fmtRelativeTime(p.daysSinceWorkedOn)}
      </td>
      <td>
        <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-icon" title="Edit" onClick={() => onEdit(p)}>
            <Pencil size={13} />
          </button>
          <button
            className="btn btn-ghost btn-icon"
            title="Archive"
            style={{ color: 'var(--ns-err)' }}
            onClick={() => onArchive(p)}
          >
            <Archive size={13} />
          </button>
        </div>
      </td>
    </tr>
  )
}

function SortableHead({ label, col, sortKey, sortDir, onClick }: {
  label: string
  col: SortKey
  sortKey: SortKey
  sortDir: SortDir
  onClick: (k: SortKey) => void
}) {
  const active = sortKey === col
  return (
    <button
      className={`sortable${active ? ' active' : ''}`}
      onClick={() => onClick(col)}
    >
      {label}
      {!active ? (
        <ArrowUpDown size={11} style={{ opacity: 0.35 }} />
      ) : sortDir === 'asc'
        ? <ArrowUp size={11} />
        : <ArrowDown size={11} />}
    </button>
  )
}

function SelectFilter({ label, value, onChange, options }: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  const hasValue = value !== '' && value !== 'all'
  return (
    <div className={`po-filt${hasValue ? ' has-value' : ''}`}>
      <label>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span className="caret">
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 4l3 3 3-3" /></svg>
      </span>
    </div>
  )
}

// ─── Workspace rates card (unchanged from pre-refactor) ─────────────────────

function WorkspaceRatesCard() {
  const [open, setOpen] = useState(false)
  return (
    <div className="card" style={{ padding: 0, marginBottom: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 14px', background: 'none', border: 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Coins size={14} style={{ color: 'var(--ns-brand-strong)' }} />
        <span style={{ fontWeight: 500, fontSize: 13 }}>Default project hourly pricing</span>
        <span className="muted" style={{ fontSize: 12 }}>
          — applies to projects without their own override
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px' }}>
          <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 10 }}>
            Add multiple entries with different validity ranges so the rate can change over time.
            Per-person rows override the all-people default.
          </p>
          <RatesTable scope="workspace" />
        </div>
      )}
    </div>
  )
}
